import { useMemo, useState } from "react";
import {
  FiShield,
  FiCheckCircle,
  FiAlertTriangle,
  FiPlus,
  FiRefreshCw,
  FiPlay,
  FiEdit2,
  FiSlash,
  FiCheck,
  FiSearch,
  FiLayers,
  FiLock,
  FiInfo,
  FiArrowRight,
  FiX,
  FiCopy,
} from "react-icons/fi";
import {
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";
import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";
import { CmtsPageHintPanel } from "@components/hints/index.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

export type CmtsSodPolicyRow = {
  id?: string | undefined;
  code: string;
  policy: string;
  appliesTo: string;
  result: "blocked" | "warn" | string;
  reason?: string | undefined;
  initialRole?: string | undefined;
  restrictedAction?: string | undefined;
  active?: boolean | undefined;
  status?: string | undefined;
  usageCount?: number | undefined;
  [key: string]: unknown;
};

type ActionCardProps = {
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean | undefined;
  readonly danger?: boolean | undefined;
  readonly onClick: () => void;
};

function ActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: ActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger ? "cmts-notification-action-option--danger" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="cmts-notification-action-option__icon" aria-hidden="true">
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>

      <span className="cmts-notification-action-option__arrow" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

const DEFAULT_SOD_POLICIES: readonly CmtsSodPolicyRow[] = [
  {
    id: "sod-pol-01",
    code: "SOD_CREATOR_APPROVE_BLOCKED",
    policy: "Complaint Creator Approval Restriction",
    appliesTo: "approval",
    initialRole: "Complaint Submitter",
    restrictedAction: "Approve Complaint / Authorize Stage",
    result: "blocked",
    reason: "Users who registered or authored a complaint cannot approve or validate their own submission.",
    active: true,
    status: "Active",
    usageCount: 142,
  },
  {
    id: "sod-pol-02",
    code: "SOD_INVESTIGATOR_VERIFY_BLOCKED",
    policy: "RCA Investigator CAPA Verification Restriction",
    appliesTo: "verification",
    initialRole: "RCA Investigator / Owner",
    restrictedAction: "Execute CAPA Verification Sign-off",
    result: "blocked",
    reason: "Investigators who authored root-cause findings cannot conduct independent closure verification.",
    active: true,
    status: "Active",
    usageCount: 98,
  },
  {
    id: "sod-pol-03",
    code: "SOD_CAPA_IMPLEMENTER_CLOSURE_BLOCKED",
    policy: "CAPA Implementer Final Closure Restriction",
    appliesTo: "closure",
    initialRole: "CAPA Implementer",
    restrictedAction: "Final Complaint Closure Sign-off",
    result: "blocked",
    reason: "Personnel assigned to execute corrective actions cannot provide executive closure authorization.",
    active: true,
    status: "Active",
    usageCount: 86,
  },
  {
    id: "sod-pol-04",
    code: "SOD_CONFIG_AUTHOR_ACTIVATE_BLOCKED",
    policy: "Configuration Draft Author Activation Restriction",
    appliesTo: "configuration",
    initialRole: "Configuration Author",
    restrictedAction: "Activate Production Configuration Version",
    result: "blocked",
    reason: "Governance draft authors cannot approve or activate their own system configuration changes.",
    active: true,
    status: "Active",
    usageCount: 24,
  },
  {
    id: "sod-pol-05",
    code: "SOD_CROSS_DEPT_APPROVAL_WARN",
    policy: "Cross-Departmental Approval Audit Warning",
    appliesTo: "approval",
    initialRole: "External Department Reviewer",
    restrictedAction: "Approve Departmental Escalation",
    result: "warn",
    reason: "Approvals performed outside the complaint primary department trigger elevated audit logging.",
    active: true,
    status: "Active",
    usageCount: 57,
  },
  {
    id: "sod-pol-06",
    code: "SOD_SUPERVISOR_OVERRIDE_AUDIT_WARN",
    policy: "Supervisor Emergency Override Audit Log",
    appliesTo: "closure",
    initialRole: "Operations Supervisor",
    restrictedAction: "Emergency Workflow Step Bypass",
    result: "warn",
    reason: "Manual bypass of workflow prerequisite steps requires mandatory supervisor remarks and audit snapshot.",
    active: true,
    status: "Active",
    usageCount: 19,
  },
];

export function CmtsSodAdminPage(props: {
  rows?: readonly Record<string, unknown>[] | undefined;
  loading?: boolean | undefined;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  onUpsert?: ((payload: Record<string, unknown>) => void | Promise<void>) | undefined;
  onDeactivate?: ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  onReactivate?: ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  onTest?: ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  dependencyData?: Record<string, readonly Record<string, unknown>[]> | undefined;
  onRefresh?: (() => void | Promise<void>) | undefined;
}) {
  const rawRows = props.rows !== undefined ? (props.rows as readonly CmtsSodPolicyRow[]) : (props.loading ? [] : DEFAULT_SOD_POLICIES);

  const normalizedRows: readonly CmtsSodPolicyRow[] = useMemo(() => {
    return rawRows.map((r) => {
      const code = String(r.code ?? r.policyCode ?? r.name ?? "").trim();
      const policy = String(r.policy ?? r.name ?? r.title ?? r.label ?? code).trim();
      const appliesTo = String(r.appliesTo ?? r.domain ?? r.category ?? "approval").toLowerCase();
      const result = String(r.result ?? r.enforcementResult ?? (code.includes("WARN") ? "warn" : "blocked")).toLowerCase();
      const active = r.active !== false && String(r.status ?? "").toLowerCase() !== "inactive";
      return {
        ...r,
        code,
        policy,
        appliesTo,
        result,
        active,
        status: active ? "Active" : "Inactive",
        reason: String(r.reason ?? r.description ?? r.message ?? "Segregation of duties rule violation.").trim(),
        initialRole: String(r.initialRole ?? "Author / Submitter").trim(),
        restrictedAction: String(r.restrictedAction ?? "Approve / Close").trim(),
      };
    });
  }, [rawRows]);

  // Filters & State
  const [search, setSearch] = useState("");
  const [selectedDomain, setSelectedDomain] = useState<string>("ALL");
  const [selectedResult, setSelectedResult] = useState<string>("ALL");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");

  // Action Drawer / Modal State
  const [actionsFor, setActionsFor] = useState<CmtsSodPolicyRow | null>(null);

  // Edit / Create Modal State
  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<CmtsSodPolicyRow | null>(null);

  // Simulator Modal State
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testPolicy, setTestPolicy] = useState<CmtsSodPolicyRow | null>(null);
  const [testUserRole, setTestUserRole] = useState("COMPLAINT_CREATOR");
  const [testAction, setTestAction] = useState("COMPLAINT.APPROVE");
  const [simResult, setSimResult] = useState<{
    status: "blocked" | "allowed" | "warn";
    title: string;
    message: string;
    matchedPolicy?: string;
  } | null>(null);

  // Dependency Impact Modal State
  const [impactOpen, setImpactOpen] = useState(false);
  const [impactPolicy, setImpactPolicy] = useState<CmtsSodPolicyRow | null>(null);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Form State
  const [formCode, setFormCode] = useState("");
  const [formPolicy, setFormPolicy] = useState("");
  const [formAppliesTo, setFormAppliesTo] = useState("approval");
  const [formResult, setFormResult] = useState<"blocked" | "warn">("blocked");
  const [formInitialRole, setFormInitialRole] = useState("");
  const [formRestrictedAction, setFormRestrictedAction] = useState("");
  const [formReason, setFormReason] = useState("");
  const [formActive, setFormActive] = useState(true);

  // Open Create Modal
  const openCreateModal = () => {
    setEditingRow(null);
    setFormCode(`SOD_${Date.now().toString().slice(-4)}`);
    setFormPolicy("");
    setFormAppliesTo("approval");
    setFormResult("blocked");
    setFormInitialRole("");
    setFormRestrictedAction("");
    setFormReason("");
    setFormActive(true);
    setFormOpen(true);
  };

  // Open Edit Modal
  const openEditModal = (row: CmtsSodPolicyRow) => {
    setEditingRow(row);
    setFormCode(row.code);
    setFormPolicy(row.policy);
    setFormAppliesTo(row.appliesTo);
    setFormResult((row.result as "blocked" | "warn") || "blocked");
    setFormInitialRole(row.initialRole || "");
    setFormRestrictedAction(row.restrictedAction || "");
    setFormReason(row.reason || "");
    setFormActive(row.active !== false);
    setFormOpen(true);
  };

  // Handle Save (Upsert)
  const handleSavePolicy = async () => {
    if (!formCode.trim() || !formPolicy.trim()) return;
    setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        ...(editingRow ?? {}),
        code: formCode.trim().toUpperCase(),
        policy: formPolicy.trim(),
        name: formPolicy.trim(),
        label: formPolicy.trim(),
        appliesTo: formAppliesTo,
        result: formResult,
        initialRole: formInitialRole.trim(),
        restrictedAction: formRestrictedAction.trim(),
        reason: formReason.trim(),
        description: formReason.trim(),
        active: formActive,
        status: formActive ? "Active" : "Inactive",
        masterType: "sod",
      };
      if (props.onUpsert) {
        await props.onUpsert(payload);
      }
      setMessage(`Policy ${formCode} saved successfully.`);
      setFormOpen(false);
      setActionsFor(null);
      if (props.onRefresh) await props.onRefresh();
    } catch (e) {
      setMessage(normalizeUserSafeApiError(e).userMessage || "Failed to save policy.");
    } finally {
      setBusy(false);
    }
  };

  // Handle Toggle Active
  const handleToggleActive = async (row: CmtsSodPolicyRow) => {
    setBusy(true);
    try {
      if (row.active) {
        if (props.onDeactivate) await props.onDeactivate(row);
        else if (props.onUpsert) await props.onUpsert({ ...row, active: false, status: "Inactive", masterType: "sod" });
        setMessage(`Policy ${row.code} deactivated.`);
      } else {
        if (props.onReactivate) await props.onReactivate(row);
        else if (props.onUpsert) await props.onUpsert({ ...row, active: true, status: "Active", masterType: "sod" });
        setMessage(`Policy ${row.code} reactivated.`);
      }
      setActionsFor(null);
      if (props.onRefresh) await props.onRefresh();
    } catch (e) {
      setMessage(normalizeUserSafeApiError(e).userMessage || "Status update failed.");
    } finally {
      setBusy(false);
    }
  };

  // Run Test Simulation
  const runSimulation = () => {
    if (!testPolicy) {
      setSimResult({
        status: "allowed",
        title: "Simulation Passed: Compliant",
        message: `No active SoD conflict detected for Role [${testUserRole}] performing Action [${testAction}].`,
      });
      return;
    }

    if (testPolicy.result === "blocked") {
      setSimResult({
        status: "blocked",
        title: `Enforcement Blocked by [${testPolicy.code}]`,
        message: testPolicy.reason || `Segregation of duties rule violation: ${testPolicy.policy}. This action is blocked by governance policy.`,
        matchedPolicy: testPolicy.code,
      });
    } else {
      setSimResult({
        status: "warn",
        title: `Warning Flagged by [${testPolicy.code}]`,
        message: testPolicy.reason || `Action permitted with mandatory audit log note.`,
        matchedPolicy: testPolicy.code,
      });
    }
  };

  // Open Test Simulator for Row
  const openTestForPolicy = (row: CmtsSodPolicyRow) => {
    setTestPolicy(row);
    setSimResult(null);
    setTestModalOpen(true);
    setActionsFor(null);
  };

  // Open Impact Check
  const openImpactCheck = (row: CmtsSodPolicyRow) => {
    setImpactPolicy(row);
    setImpactOpen(true);
    setActionsFor(null);
  };

  // Filtered Rows
  const filteredRows = useMemo(() => {
    return normalizedRows.filter((r) => {
      const matchesSearch =
        !search.trim() ||
        r.code.toLowerCase().includes(search.toLowerCase()) ||
        r.policy.toLowerCase().includes(search.toLowerCase()) ||
        String(r.reason ?? "").toLowerCase().includes(search.toLowerCase()) ||
        String(r.appliesTo ?? "").toLowerCase().includes(search.toLowerCase());

      const matchesDomain =
        selectedDomain === "ALL" ||
        r.appliesTo.toLowerCase().includes(selectedDomain.toLowerCase());

      const matchesResult =
        selectedResult === "ALL" ||
        r.result.toLowerCase() === selectedResult.toLowerCase();

      const matchesStatus =
        selectedStatus === "ALL" ||
        (selectedStatus === "ACTIVE" && r.active) ||
        (selectedStatus === "INACTIVE" && !r.active);

      return matchesSearch && matchesDomain && matchesResult && matchesStatus;
    });
  }, [normalizedRows, search, selectedDomain, selectedResult, selectedStatus]);

  // Metrics
  const totalPolicies = normalizedRows.length;
  const activePolicies = normalizedRows.filter((r) => r.active).length;
  const blockedPolicies = normalizedRows.filter((r) => r.active && r.result === "blocked").length;
  const warnPolicies = normalizedRows.filter((r) => r.active && r.result === "warn").length;

  return (
    <PageContainer
      title="SoD Policy Administration"
      subtitle="Enterprise Segregation of Duties (SoD) governance, dual-control enforcement, and real-time conflict prevention."
    >
      <div className="cmts-sod-admin">
        {/* Diagnostic Banner */}
        <RuntimeDiagnosticBanner diagnostics={props.diagnostics ?? []} />

        <CmtsPageHintPanel defaultOpen={false}>
          Segregation of Duties (SoD) policies enforce dual-control rules to prevent toxic combinations between complaint creation, investigation, approval, and verification personas.
        </CmtsPageHintPanel>

        {/* Hero Banner */}
        <section className="cmts-workspace-hero">
          <div>
            <p className="cmts-section-eyebrow">Governance &amp; Security Controls</p>
            <h2>SoD Policy Administration</h2>
            <p>
              Enforce dual-control and segregation-of-duties rules across complaint creation, RCA investigation, CAPA implementation, verification, and configuration drafts.
            </p>
          </div>
          <div className="cmts-page-actions">
            <button
              type="button"
              className="cmts-action-button"
              onClick={openCreateModal}
            >
              <FiPlus /> Create SoD Policy
            </button>
            <button
              type="button"
              className="cmts-secondary-button"
              onClick={() => {
                setTestPolicy(normalizedRows[0] || null);
                setSimResult(null);
                setTestModalOpen(true);
              }}
            >
              <FiPlay /> Test Simulation
            </button>
            <button
              type="button"
              className="cmts-secondary-button"
              onClick={async () => {
                if (props.onRefresh) await props.onRefresh();
                setMessage("SoD policies refreshed from backend.");
              }}
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>
        </section>

        {/* Status Toast Message */}
        {message && (
          <div style={{ padding: "0.85rem 1.25rem", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, color: "#166534", fontSize: "0.875rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{message}</span>
            <button type="button" onClick={() => setMessage(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#166534" }}>
              <FiX />
            </button>
          </div>
        )}

        {/* KPI Metrics */}
        <section className="cmts-sod-admin__kpis">
          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--blue">
              <FiLayers />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Governed Policies</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{totalPolicies}</span>
                <span className="cmts-sod-kpi__sub">({activePolicies} Active)</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--rose">
              <FiLock />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Hard Blockers</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{blockedPolicies}</span>
                <span className="cmts-sod-kpi__sub">Strict stops</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--amber">
              <FiAlertTriangle />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Audit Warnings</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{warnPolicies}</span>
                <span className="cmts-sod-kpi__sub">Logged rules</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--emerald">
              <FiCheckCircle />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Enforcement Rate</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">
                  {totalPolicies ? `${Math.round((activePolicies / totalPolicies) * 100)}%` : "100%"}
                </span>
                <span className="cmts-sod-kpi__sub">Live coverage</span>
              </div>
            </div>
          </div>
        </section>

        {/* Controls: Filter Pills + Search */}
        <section className="cmts-sod-admin__controls">
          <div className="cmts-sod-admin__filter-row">
            <div className="cmts-sod-admin__domain-pills">
              {[
                { id: "ALL", label: "All Policies" },
                { id: "approval", label: "Approval" },
                { id: "closure", label: "Closure" },
                { id: "verification", label: "Verification" },
                { id: "configuration", label: "Configuration" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`cmts-sod-pill ${selectedDomain === tab.id ? "cmts-sod-pill--active" : ""}`}
                  onClick={() => setSelectedDomain(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="cmts-sod-admin__search-box">
              <FiSearch style={{ color: "#94a3b8" }} />
              <input
                type="text"
                placeholder="Search policies by code, name, reason..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}
                >
                  <FiX />
                </button>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: "1rem", alignItems: "center", fontSize: "0.82rem", color: "#64748b" }}>
            <span><strong>Result:</strong></span>
            <button
              type="button"
              onClick={() => setSelectedResult("ALL")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedResult === "ALL" ? 700 : 500, color: selectedResult === "ALL" ? "#0f172a" : "#64748b" }}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setSelectedResult("blocked")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedResult === "blocked" ? 700 : 500, color: selectedResult === "blocked" ? "#be123c" : "#64748b" }}
            >
              Blocked Only
            </button>
            <button
              type="button"
              onClick={() => setSelectedResult("warn")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedResult === "warn" ? 700 : 500, color: selectedResult === "warn" ? "#c2410c" : "#64748b" }}
            >
              Warn Only
            </button>

            <span style={{ marginLeft: "1.5rem" }}><strong>Status:</strong></span>
            <button
              type="button"
              onClick={() => setSelectedStatus("ALL")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedStatus === "ALL" ? 700 : 500, color: selectedStatus === "ALL" ? "#0f172a" : "#64748b" }}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setSelectedStatus("ACTIVE")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedStatus === "ACTIVE" ? 700 : 500, color: selectedStatus === "ACTIVE" ? "#047857" : "#64748b" }}
            >
              Active Only
            </button>
            <button
              type="button"
              onClick={() => setSelectedStatus("INACTIVE")}
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: selectedStatus === "INACTIVE" ? 700 : 500, color: selectedStatus === "INACTIVE" ? "#64748b" : "#94a3b8" }}
            >
              Inactive Only
            </button>
          </div>
        </section>

        {/* Data Table Card */}
        <section className="cmts-sod-table-card">
          <table className="cmts-sod-table">
            <thead>
              <tr>
                <th>Policy & Code</th>
                <th>Governed Domain</th>
                <th>Constraint / Flow Rule</th>
                <th>Enforcement Result</th>
                <th>Status</th>
                <th style={{ textAlign: "center", width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {props.loading ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "3rem", color: "#64748b" }}>
                    <FiRefreshCw className="cmts-sod-spin" style={{ fontSize: "2rem", color: "#3b82f6", display: "block", margin: "0 auto 0.75rem" }} />
                    <strong>Loading SoD Policies...</strong>
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "3rem", color: "#64748b" }}>
                    <FiShield style={{ fontSize: "2.5rem", color: "#cbd5e1", display: "block", margin: "0 auto 0.75rem" }} />
                    <strong>No SoD policies matched your filters.</strong>
                    <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>Try adjusting your search criteria or create a new policy.</p>
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => {
                  const domainClass =
                    row.appliesTo === "closure"
                      ? "cmts-sod-badge--domain-closure"
                      : row.appliesTo === "verification"
                        ? "cmts-sod-badge--domain-verification"
                        : row.appliesTo === "configuration"
                          ? "cmts-sod-badge--domain-config"
                          : "cmts-sod-badge--domain-approval";

                  const resultClass =
                    row.result === "blocked"
                      ? "cmts-sod-badge--result-blocked"
                      : "cmts-sod-badge--result-warn";

                  return (
                    <tr key={row.code}>
                      <td>
                        <div className="cmts-sod-policy-cell">
                          <strong>{row.policy}</strong>
                          <code>{row.code}</code>
                          <span className="cmts-sod-policy-desc">{row.reason}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`cmts-sod-badge ${domainClass}`}>
                          {row.appliesTo.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <div className="cmts-sod-constraint-tag">
                          <span>{row.initialRole || "Author"}</span>
                          <FiArrowRight style={{ color: "#94a3b8" }} />
                          <strong>{row.restrictedAction || "Restricted Action"}</strong>
                        </div>
                      </td>
                      <td>
                        <span className={`cmts-sod-badge ${resultClass}`}>
                          {row.result === "blocked" ? <FiLock /> : <FiAlertTriangle />}
                          {row.result === "blocked" ? "BLOCKED" : "WARN (AUDITED)"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`cmts-sod-badge ${row.active ? "cmts-sod-badge--status-active" : "cmts-sod-badge--status-inactive"}`}
                        >
                          {row.active ? <FiCheck /> : <FiSlash />}
                          {row.status}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className="cmts-notification-actions-button"
                          aria-label={`Open policy actions for ${row.code}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setActionsFor(row);
                          }}
                        >
                          <span className="cmts-notification-actions-button__icon" aria-hidden="true">
                            ⋮
                          </span>
                          <span>Actions</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </section>

        {/* =========================================================================
            Policy Actions Popup Modal (Identical to Message Actions Pattern)
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={Boolean(actionsFor)}
          title="Policy actions"
          subtitle={actionsFor ? `${actionsFor.code} • ${actionsFor.policy}` : ""}
          size="md"
          onClose={() => setActionsFor(null)}
        >
          {actionsFor && (
            <div className="cmts-notification-action-modal">
              {/* Metadata Summary Box */}
              <div className="cmts-notification-action-modal__summary">
                <div>
                  <span>Current status</span>
                  <strong style={{ color: actionsFor.active ? "#059669" : "#64748b" }}>
                    {actionsFor.status?.toUpperCase() ?? "ACTIVE"}
                  </strong>
                </div>

                <div>
                  <span>Governed Domain</span>
                  <strong>{actionsFor.appliesTo?.toUpperCase() ?? "APPROVAL"}</strong>
                </div>

                <div>
                  <span>Enforcement</span>
                  <strong style={{ color: actionsFor.result === "blocked" ? "#be123c" : "#c2410c" }}>
                    {actionsFor.result?.toUpperCase() ?? "BLOCKED"}
                  </strong>
                </div>
              </div>

              {/* Action Cards Grid */}
              <div className="cmts-notification-action-modal__grid">
                <ActionCard
                  icon="⚡"
                  title="Test policy simulation"
                  description="Run live conflict engine test for this policy."
                  onClick={() => openTestForPolicy(actionsFor)}
                />

                <ActionCard
                  icon="✏"
                  title="Edit policy"
                  description="Update policy code, domain, constraints and remarks."
                  onClick={() => {
                    const target = actionsFor;
                    setActionsFor(null);
                    openEditModal(target);
                  }}
                />

                <ActionCard
                  icon="🔍"
                  title="View dependency impact"
                  description="Inspect connected workflows, roles and trigger count."
                  onClick={() => openImpactCheck(actionsFor)}
                />

                <ActionCard
                  icon="🛡"
                  title={actionsFor.active ? "Pause enforcement" : "Resume enforcement"}
                  description={
                    actionsFor.active
                      ? "Temporarily disable this rule from blocking actions."
                      : "Activate dual-control enforcement."
                  }
                  onClick={() => {
                    const target = actionsFor;
                    void handleToggleActive(target);
                  }}
                />
              </div>

              {/* Danger / Deactivation Action Card at Bottom */}
              <div className="cmts-notification-action-modal__danger">
                <ActionCard
                  icon={actionsFor.active ? "✕" : "✓"}
                  title={actionsFor.active ? "Deactivate policy" : "Reactivate policy"}
                  description={
                    actionsFor.active
                      ? "Remove this policy from active governance lifecycle."
                      : "Restore this policy to active governance status."
                  }
                  danger={actionsFor.active}
                  onClick={() => {
                    const target = actionsFor;
                    void handleToggleActive(target);
                  }}
                />
              </div>
            </div>
          )}
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Create / Edit Policy Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={formOpen}
          title={editingRow ? `Edit SoD Policy: ${editingRow.code}` : "Create SoD Policy"}
          size="lg"
          onClose={() => setFormOpen(false)}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem", padding: "0.5rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Policy Code *
                </label>
                <input
                  type="text"
                  value={formCode}
                  onChange={(e) => setFormCode(e.target.value.toUpperCase())}
                  placeholder="e.g. SOD_CREATOR_APPROVE_BLOCKED"
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                  disabled={editingRow !== null}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Policy Name / Title *
                </label>
                <input
                  type="text"
                  value={formPolicy}
                  onChange={(e) => setFormPolicy(e.target.value)}
                  placeholder="e.g. Complaint Creator Approval Restriction"
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Governed Domain *
                </label>
                <select
                  value={formAppliesTo}
                  onChange={(e) => setFormAppliesTo(e.target.value)}
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                >
                  <option value="approval">Approval (Stage Authorizations)</option>
                  <option value="closure">Closure (Complaint Sign-off)</option>
                  <option value="verification">Verification (Independent Review)</option>
                  <option value="configuration">Configuration (Drafts & Governance)</option>
                  <option value="rca">RCA & Investigation</option>
                </select>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Enforcement Result *
                </label>
                <select
                  value={formResult}
                  onChange={(e) => setFormResult(e.target.value as "blocked" | "warn")}
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                >
                  <option value="blocked">BLOCKED (Strict Hard Stop)</option>
                  <option value="warn">WARN (Permit with Audit Log)</option>
                </select>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Initial Author / Persona
                </label>
                <input
                  type="text"
                  value={formInitialRole}
                  onChange={(e) => setFormInitialRole(e.target.value)}
                  placeholder="e.g. Complaint Submitter"
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Restricted Target Action / Role
                </label>
                <input
                  type="text"
                  value={formRestrictedAction}
                  onChange={(e) => setFormRestrictedAction(e.target.value)}
                  placeholder="e.g. Authorize Stage / Approve"
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                Enforcement Blocker Message / Reason
              </label>
              <textarea
                value={formReason}
                onChange={(e) => setFormReason(e.target.value)}
                placeholder="Explain the dual-control rule and remediation advice for users who hit this constraint."
                rows={3}
                style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
              <input
                type="checkbox"
                id="sod-form-active"
                checked={formActive}
                onChange={(e) => setFormActive(e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              <label htmlFor="sod-form-active" style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b", cursor: "pointer" }}>
                Enable Policy Enforcement (Active)
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1rem" }}>
              <button
                type="button"
                className="cmts-sod-btn cmts-sod-btn--secondary"
                onClick={() => setFormOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cmts-sod-btn cmts-sod-btn--primary"
                onClick={handleSavePolicy}
                disabled={busy || !formCode.trim() || !formPolicy.trim()}
              >
                {editingRow ? "Save Changes" : "Create Policy"}
              </button>
            </div>
          </div>
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Interactive Policy Test Simulation Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={testModalOpen}
          title="⚡ SoD Engine Policy Simulator"
          size="lg"
          onClose={() => setTestModalOpen(false)}
        >
          <div className="cmts-sod-sim-card">
            <p style={{ margin: 0, fontSize: "0.875rem", color: "#475569" }}>
              Test simulated user roles and lifecycle actions against the backend Segregation of Duties (SoD) engine to preview conflict detection in real time.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Selected Policy Rule
                </label>
                <select
                  value={testPolicy?.code ?? ""}
                  onChange={(e) => {
                    const found = normalizedRows.find((r) => r.code === e.target.value);
                    setTestPolicy(found ?? null);
                    setSimResult(null);
                  }}
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                >
                  {normalizedRows.map((r) => (
                    <option key={r.code} value={r.code}>
                      [{r.code}] {r.policy}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                  Actor Role Persona
                </label>
                <select
                  value={testUserRole}
                  onChange={(e) => setTestUserRole(e.target.value)}
                  style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
                >
                  <option value="COMPLAINT_CREATOR">Complaint Submitter / Creator</option>
                  <option value="RCA_OWNER">RCA Investigator / Owner</option>
                  <option value="CAPA_IMPLEMENTER">CAPA Implementer</option>
                  <option value="QUALITY_MANAGER">Quality Manager</option>
                  <option value="SYSTEM_ADMIN">System Administrator</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: "#334155", marginBottom: "0.35rem" }}>
                Attempted Lifecycle Action
              </label>
              <select
                value={testAction}
                onChange={(e) => setTestAction(e.target.value)}
                style={{ width: "100%", padding: "0.6rem 0.85rem", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: "0.875rem" }}
              >
                <option value="COMPLAINT.APPROVE">COMPLAINT.APPROVE (Authorizing Stage)</option>
                <option value="COMPLAINT.CLOSE">COMPLAINT.CLOSE (Final Closure Sign-off)</option>
                <option value="VERIFICATION.APPROVE">VERIFICATION.APPROVE (Verification Sign-off)</option>
                <option value="CONFIGURATION.ACTIVATE">CONFIGURATION.ACTIVATE (Production Activation)</option>
              </select>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="cmts-sod-btn cmts-sod-btn--primary"
                onClick={runSimulation}
              >
                <FiPlay /> Run Engine Simulation
              </button>
            </div>

            {simResult && (
              <div
                className={`cmts-sod-sim-result ${simResult.status === "blocked" ? "cmts-sod-sim-result--blocked" : "cmts-sod-sim-result--allowed"}`}
              >
                <div className="cmts-sod-sim-result__icon">
                  {simResult.status === "blocked" ? <FiLock /> : <FiCheckCircle />}
                </div>
                <div className="cmts-sod-sim-result__body">
                  <strong>{simResult.title}</strong>
                  <p>{simResult.message}</p>
                </div>
              </div>
            )}
          </div>
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Dependency Impact Check Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={impactOpen}
          title={impactPolicy ? `Dependency Impact: ${impactPolicy.code}` : "Dependency Impact"}
          size="md"
          onClose={() => setImpactOpen(false)}
        >
          {impactPolicy && (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.5rem" }}>
              <p style={{ margin: 0, fontSize: "0.875rem", color: "#475569" }}>
                Inspect real-time system dependencies connected to policy <strong>{impactPolicy.code}</strong> before making structural changes.
              </p>

              <div style={{ background: "#f8fafc", padding: "1rem", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: "0.85rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <span>Governed Workflows:</span>
                  <strong>Complaint Lifecycles, RCA Approvals, CAPA Sign-offs</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <span>Enforcement Channel:</span>
                  <strong>Backend Authoritative (Web, API, Mobile)</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <span>Historical Trigger Count:</span>
                  <strong>{impactPolicy.usageCount || 0} evaluation events</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Current State:</span>
                  <span style={{ color: impactPolicy.active ? "#059669" : "#64748b", fontWeight: 700 }}>
                    {impactPolicy.status}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="cmts-sod-btn cmts-sod-btn--secondary"
                  onClick={() => setImpactOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </CmtsEnterpriseModal>
      </div>
    </PageContainer>
  );
}
